import queryString from 'query-string';
import { APP_CONFIG } from '../APP_CONFIG';
import { createLocation, LocationDescriptorObject } from 'history';

export type RoutableProps = Record<string, string | (string | undefined)>;

export type RoutePathsWithParams<TParams extends RoutableProps> = [string] & {
  _typeSafetyPrivateProperty: TParams | never;
};
export type RouteEntry = {
  /**
   * Used as the `path` prop in a ReactRouter <Route /> component.
   *
   * @example
   * <Switch>
   *  <Route path={ROUTES.appShell.logout.route} component={LogoutScene} />
   * </Switch>
   */
  route: [string];

  /**
   * Creates a Location object to the given route. This is intended to be used in
   * <Link /> components or calls to history.push/replace.
   *
   * THIS IS NOT TO BE USED AS AN HREF in <a href={}/> TAGS.
   * Doing so will cause the SPA app to do a full page reload instead
   * of using fast SPA routing.
   *
   * @example
   * <Link to={ROUTES.appShell.logout.locationFor()}>
   *
   * // -- OR --
   *
   * const history = useHistory();
   * history.push(ROUTES.appShell.logout.locationFor();
   */
  locationFor: () => LocationDescriptorObject;

  /**
   * WARNING: This is generally not the url to use for links/navigation!!
   *
   * @see locationFor
   *
   * Use with great caution. Not intended for regular hrefs or links but
   * only in rare occasions when an absolute URL is needed.
   */
  externalUrlFor: () => string;
};

export type ExternalRouteEntry = {
  externalUrlFor: () => string;
};

export type RouteEntryWithParams<TParams extends RoutableProps> = {
  /**
   * Used as the `path` prop in a ReactRouter <Route /> component.
   *
   * @example
   * <Switch>
   *  <RouteWithParams path={ROUTES.myModule.edit.route} component={EditStuff} />
   * </Switch>
   */
  route: RoutePathsWithParams<TParams>;

  /**
   * Creates a Location object to the given route. This is intended to be used in
   * <Link /> components or calls to history.push/replace.
   *
   * THIS IS NOT TO BE USED AS AN HREF in <a href={}/> TAGS.
   * Doing so will cause the SPA app to do a full page reload instead
   * of using fast SPA routing.
   *
   * @example
   * <Link to={ROUTES.myModule.edit.locationFor({id: 'FOO'})}>
   *
   * // -- OR --
   *
   * const history = useHistory();
   * history.push(ROUTES.myModule.edit.locationFor({id: 'FOO'});
   */
  locationFor: (params: TParams) => LocationDescriptorObject;

  /**
   * WARNING: This is generally not the url to use for links/navigation!!
   *
   * Use with great caution. Not intended for regular hrefs or links but
   * only in rare occasions when an absolute URL is needed.
   */
  externalUrlFor: (params: TParams) => string;
};

export type ExternalRouteEntryWithParams<TParams extends RoutableProps> = {
  externalUrlFor: (params: TParams) => string;
};

const absoluteUrlFor = (path: string) => `${window.location.protocol}//${window.location.host}${path}`;
const verifyRoutePath = (urlOrPath: string, options?: { allowParams?: boolean; absolute?: boolean }) => {
  // Only run this validation locally, once deployed, it can be skipped.
  if (!['test', 'dev'].includes(APP_CONFIG.build.environment)) return;

  const path = options?.absolute ? urlOrPath.replace(/^(https?:)?\/\/[^\/]+\/?/, '/') : urlOrPath;

  if (path.includes('//')) {
    throw new Error(
      `Your Route path contains a double slash. Check that your paths do not start with a slash:\n ${urlOrPath}`
    );
  }
  if (!path.startsWith('/')) {
    throw new Error(`Your Route path must begin with a slash:\n${urlOrPath}`);
  }
  if (path.substr(1).endsWith('/')) {
    throw new Error(`Your Route path ends with a slash. Check that your paths do not end with a slash:\n ${urlOrPath}`);
  }
  if (options?.absolute && urlOrPath === path) {
    throw new Error(`Your external Route URL must contain a protocol or begin with a double slash:\n ${urlOrPath}`);
  }
  if (path.includes('?')) {
    throw new Error(
      `Your Route path contains a question mark. Please supply your query params in an object:\n ${urlOrPath}`
    );
  }
  if (!options?.allowParams && path.includes(':')) {
    throw new Error(
      `Your Route path contains parameters. Use 'withParams' instead of 'plain' route:\n ${path} - ${urlOrPath}`
    );
  }
};

const sub = (urlPath: string, params: RoutableProps = {}) => {
  const reduced = Object.entries(params).reduce<{ url: string; query: Record<string, string> }>(
    (acc, [key, value]) => {
      if (value === undefined) return acc;
      const newPath = acc.url.replace(new RegExp(`:${key}\\b`, 'g'), encodeURIComponent(String(value)));
      return {
        url: newPath,
        query:
          newPath === acc.url && !['', undefined].includes(value) ? { ...acc.query, [key]: String(value) } : acc.query
      };
    },
    { url: urlPath, query: {} }
  );

  return (
    queryString
      .stringifyUrl(reduced, { arrayFormat: 'comma' })
      // always replace escaped commas with real commas for more readable URLs
      .replace(/%2C/g, ',')
  );
};

// TODO: Would it be more appropriate to use Location objects for internal (routed) destinations
// instead of URLs. Reason would be that Location objects are generally accepted by react-router functions
// and coders in the project would be less-likely to mistakenly use them in non-router links like <a/>'s.
// For example:
// <Link to={ROUTES.myModule.home.locationFor()} /> // Works!
// <a href={ROUTES.myModule.home.locationFor()} /> // Would not work! Should use Link instead to avoid a full-page-reload.
export const routeHelper = {
  /**
   * INTERNAL route with no path or query parameters.
   */
  external(url: string): ExternalRouteEntry {
    verifyRoutePath(url, { absolute: true });
    return {
      externalUrlFor: () => url
    };
  },

  /**
   * EXTERNAL route with path or query parameters.
   *
   *  @example
   * // In this example, `vin` is a query parameter and `sellerNumber` is a path parameter
   * routeHelper.withParams<{vin: string}>('/myPath/:sellerNumber')
   *
   */
  externalWithParams<TParams extends RoutableProps>(url: string): ExternalRouteEntryWithParams<TParams> {
    verifyRoutePath(url, { allowParams: true, absolute: true });
    return {
      externalUrlFor: (params: TParams) => sub(url, params)
    };
  },

  /**
   * INTERNAL route with no path or query parameters.
   */
  plain(path: string): RouteEntry {
    verifyRoutePath(path);
    return {
      route: [path],
      locationFor: () => createLocation(path),
      externalUrlFor: () => absoluteUrlFor(path)
    };
  },

  /**
   * INTERNAL route with path or query parameters.
   *
   *  @example
   * // In this example, `vin` is a query parameter and `sellerNumber` is a path parameter
   * routeHelper.withParams<{vin: string}>('/myPath/:sellerNumber')
   *
   */
  withParams<TParams extends RoutableProps>(path: string): RouteEntryWithParams<TParams> {
    verifyRoutePath(path, { allowParams: true });
    return {
      route: [path] as RoutePathsWithParams<TParams>,
      locationFor: params => createLocation(sub(path, params)),
      externalUrlFor: params => absoluteUrlFor(sub(path, params))
    };
  }
};
